import React from 'react';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  LinearProgress,
  alpha,
} from '@mui/material';
import { keyframes } from '@emotion/react';

export type LoadingVariant = 'circular' | 'linear' | 'dots' | 'pulse' | 'minimal';

export interface ContextLoadingProps {
  /** Loading message to display */
  message?: string;
  /** Visual variant for the loading indicator */
  variant?: LoadingVariant;
  /** Size of the loading indicator */
  size?: 'small' | 'medium' | 'large';
  /** Color of the loading indicator */
  color?: 'primary' | 'secondary' | 'inherit';
  /** Show progress percentage if available */
  progress?: number;
  /** Additional descriptive text */
  description?: string;
  /** Compact mode for inline loading */
  compact?: boolean;
  /** Center the loading indicator */
  centered?: boolean;
  /** Custom styling */
  sx?: object;
}

// Animation keyframes
const pulseAnimation = keyframes`
  0% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
`;

const dotsAnimation = keyframes`
  0%, 20% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
  80%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
`;

/**
 * ContextLoading - Specialized loading component for context initialization
 * 
 * Features:
 * - Multiple visual variants (circular, linear, dots, pulse, minimal)
 * - Progress indication support
 * - Compact and centered layouts
 * - Context-aware styling
 * - Smooth animations with theme integration
 */
export const ContextLoading: React.FC<ContextLoadingProps> = ({
  message = 'Loading...',
  variant = 'circular',
  size = 'medium',
  color = 'primary',
  progress,
  description,
  compact = false,
  centered = true,
  sx = {},
}) => {
  const sizeMap = {
    small: { indicator: 24, text: 'body2' as const },
    medium: { indicator: 40, text: 'body1' as const },
    large: { indicator: 56, text: 'h6' as const },
  };

  const currentSize = sizeMap[size];

  const renderLoadingIndicator = () => {
    switch (variant) {
      case 'circular':
        return (
          <CircularProgress
            size={currentSize.indicator}
            color={color}
            {...(progress !== undefined && { variant: 'determinate', value: progress })}
          />
        );

      case 'linear':
        return (
          <Box sx={{ width: '100%', maxWidth: 300 }}>
            <LinearProgress
              color={color}
              {...(progress !== undefined && { variant: 'determinate', value: progress })}
            />
          </Box>
        );

      case 'dots':
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: `${color}.main`,
                  animation: `${dotsAnimation} 1.4s ease-in-out infinite`,
                  animationDelay: `${index * 0.16}s`,
                }}
              />
            ))}
          </Box>
        );

      case 'pulse':
        return (
          <Box
            sx={{
              width: currentSize.indicator,
              height: currentSize.indicator,
              borderRadius: '50%',
              bgcolor: (theme) => alpha(theme.palette[color].main, 0.2),
              border: (theme) => `2px solid ${theme.palette[color].main}`,
              animation: `${pulseAnimation} 2s ease-in-out infinite`,
            }}
          />
        );

      case 'minimal':
        return (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 'medium',
              '&::after': {
                content: '"..."',
                animation: `${dotsAnimation} 1.5s ease-in-out infinite`,
              },
            }}
          >
            {message}
          </Typography>
        );

      default:
        return <CircularProgress size={currentSize.indicator} color={color} />;
    }
  };

  const containerProps = {
    sx: {
      display: 'flex',
      flexDirection: compact ? 'row' : 'column',
      alignItems: 'center',
      justifyContent: centered ? 'center' : 'flex-start',
      gap: compact ? 1 : 2,
      padding: compact ? 1 : 2,
      minHeight: compact ? 'auto' : 120,
      textAlign: 'center' as const,
      ...sx,
    },
  };

  if (variant === 'minimal') {
    return (
      <Box {...containerProps}>
        {renderLoadingIndicator()}
      </Box>
    );
  }

  return (
    <Box {...containerProps}>
      {renderLoadingIndicator()}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant={currentSize.text}
          color="text.primary"
          sx={{ fontWeight: 'medium' }}
        >
          {message}
        </Typography>
        
        {progress !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {Math.round(progress)}%
          </Typography>
        )}
        
        {description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ maxWidth: 300, textAlign: 'center' }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/**
 * Hook for context loading states with automatic progress simulation
 */
export const useContextLoading = (isLoading: boolean, estimatedDuration?: number) => {
  const [progress, setProgress] = React.useState(0);
  const [message, setMessage] = React.useState('Initializing...');

  React.useEffect(() => {
    if (!isLoading) {
      setProgress(100);
      return;
    }

    setProgress(0);
    setMessage('Initializing...');

    if (!estimatedDuration) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (100 / (estimatedDuration / 100));
        
        // Update message based on progress
        if (newProgress < 30) {
          setMessage('Loading configuration...');
        } else if (newProgress < 60) {
          setMessage('Connecting to services...');
        } else if (newProgress < 90) {
          setMessage('Finalizing setup...');
        } else {
          setMessage('Almost ready...');
        }
        
        return Math.min(newProgress, 95); // Cap at 95% until actually loaded
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isLoading, estimatedDuration]);

  return { progress, message };
};

/**
 * Context loading with automatic retries
 */
export interface ContextLoadingWithRetryProps extends ContextLoadingProps {
  /** Number of retry attempts */
  retryCount?: number;
  /** Maximum number of retries */
  maxRetries?: number;
  /** Retry function */
  onRetry?: () => void;
  /** Show retry information */
  showRetryInfo?: boolean;
}

export const ContextLoadingWithRetry: React.FC<ContextLoadingWithRetryProps> = ({
  retryCount = 0,
  maxRetries = 3,
  onRetry,
  showRetryInfo = true,
  message = 'Loading...',
  description,
  ...props
}) => {
  const retryMessage = retryCount > 0 
    ? `${message} (Attempt ${retryCount + 1}/${maxRetries + 1})`
    : message;

  const retryDescription = showRetryInfo && retryCount > 0
    ? `${description ? description + ' ' : ''}Retrying connection...`
    : description;

  return (
    <ContextLoading
      {...props}
      message={retryMessage}
      description={retryDescription}
    />
  );
};

export default ContextLoading;